// ---------------------------------------------------------------------------
// O RENDERIZADOR ÚNICO.
//
// Recebe o layout (dado) + o pedido e devolve as linhas prontas. Nada aqui
// conhece impressora nem navegador: é função pura.
//
//   OPS      -> aplicarLinhasNaImpressora(printer, linhas)  (ESC/POS)
//   Central  -> <PreviewComanda linhas={...} />              (HTML)
//
// É por ser o MESMO código nos dois lados que o preview não pode mentir.
// Ver o aviso no topo de tipos.ts sobre as três cópias e o hash do build.
//
// Fidelidade ao que já é impresso hoje: o espaçamento de coluna abaixo é uma
// reimplementação literal de tableCustom() do node-thermal-printer 4.4.1
// (lib/core.js, linhas 302-372), inclusive as esquisitices — largura de célula
// fracionária, o loop "spaces - 1" do centralizado e o corte com recursão na
// segunda linha. Se isso divergir, o preview passa a mentir.
// ---------------------------------------------------------------------------

import {
  Alinhamento,
  BlocoComanda,
  CampoComanda,
  ChaveCampo,
  ColunaComanda,
  CondicaoCampo,
  LayoutComanda,
  LinhaComanda,
  PedidoParaImpressao,
  ProdutoParaImpressao,
} from "./tipos";

/** Célula de tabela, no mesmo formato que tableCustom recebe. */
interface CelulaTabela {
  texto: string;
  alinhamento: Alinhamento;
  /** Fração da largura do papel. */
  largura: number;
}

// ---------------------------------------------------------------------------
// Espaçamento de coluna — cópia literal de tableCustom()
// ---------------------------------------------------------------------------

/**
 * Monta UMA linha de tabela do jeito exato do node-thermal-printer.
 * Devolve também as sobras, porque a biblioteca corta o que não coube e
 * reimprime numa linha seguinte (recursão em tableCustom).
 */
function montarLinhaDeTabela(
  celulas: CelulaTabela[],
  larguraPapel: number
): { linha: string; sobra: CelulaTabela[] | null } {
  let larguraCelulaPadrao = larguraPapel / celulas.length;
  let saida = "";
  const sobra: CelulaTabela[] = [];
  let temSobra = false;

  for (let i = 0; i < celulas.length; i++) {
    const celula = celulas[i];
    let texto = String(celula.texto);
    const larguraCelula = celula.largura
      ? larguraPapel * celula.largura
      : larguraCelulaPadrao;

    let cortou = false;
    let textoOriginal = texto;
    // A biblioteca corta em (larguraCelula - 1) quando o texto não cabe.
    if (larguraCelula < texto.length) {
      cortou = true;
      texto = texto.substring(0, larguraCelula - 1);
    }

    if (celula.alinhamento === "centro") {
      const espacos = (larguraCelula - texto.length) / 2;
      for (let j = 0; j < espacos; j++) saida += " ";
      if (texto !== "") saida += texto;
      // Sim, "espacos - 1": é o que a biblioteca faz. Mantido de propósito.
      for (let j = 0; j < espacos - 1; j++) saida += " ";
    } else if (celula.alinhamento === "direita") {
      const espacos = larguraCelula - texto.length;
      for (let j = 0; j < espacos; j++) saida += " ";
      if (texto !== "") saida += texto;
    } else {
      if (texto !== "") saida += texto;
      const espacos = larguraCelula - texto.length;
      for (let j = 0; j < espacos; j++) saida += " ";
    }

    if (cortou) {
      temSobra = true;
      sobra.push({
        texto: textoOriginal.substring(larguraCelula - 1),
        alinhamento: celula.alinhamento,
        largura: celula.largura,
      });
    } else {
      sobra.push({
        texto: "",
        alinhamento: celula.alinhamento,
        largura: celula.largura,
      });
    }
  }

  return { linha: saida, sobra: temSobra ? sobra : null };
}

/** Monta a tabela inteira, seguindo a recursão da segunda linha. */
function montarTabela(
  celulas: CelulaTabela[],
  larguraPapel: number
): string[] {
  const linhas: string[] = [];
  let atual: CelulaTabela[] | null = celulas;
  // Trava de segurança: nenhuma comanda real precisa de mais que isso, e uma
  // configuração ruim não pode virar laço infinito na cozinha.
  let voltas = 0;
  while (atual && voltas < 20) {
    const { linha, sobra } = montarLinhaDeTabela(atual, larguraPapel);
    linhas.push(linha);
    atual = sobra;
    voltas++;
  }
  return linhas;
}

// ---------------------------------------------------------------------------
// Quebra de linha
// ---------------------------------------------------------------------------

/**
 * Quebra por CARACTERE, na largura útil.
 *
 * O código de hoje usa BreakLine.NONE, ou seja: não quebra em software e
 * deixa a impressora quebrar no fim da coluna física — que é quebra por
 * caractere. Fazer a mesma quebra aqui não muda o papel; muda só QUEM faz,
 * e é o que permite ao preview mostrar a quebra certa.
 *
 * Em largura dupla cabe metade dos caracteres — por isso larguraUtil.
 */
export function quebrarTexto(
  texto: string,
  larguraColunas: number,
  larguraDupla: boolean
): string[] {
  const larguraUtil = Math.max(
    1,
    Math.floor(larguraDupla ? larguraColunas / 2 : larguraColunas)
  );
  if (texto.length <= larguraUtil) return [texto];
  const partes: string[] = [];
  for (let i = 0; i < texto.length; i += larguraUtil) {
    partes.push(texto.substring(i, i + larguraUtil));
  }
  return partes;
}

// ---------------------------------------------------------------------------
// Resolução de valores
// ---------------------------------------------------------------------------

function texto(valor: unknown): string {
  if (valor === null || valor === undefined) return "";
  return String(valor);
}

function ehNumeroDiferenteDeZero(valor: string): boolean {
  if (!valor) return false;
  const numero = Number(valor.replace(/\./g, "").replace(",", "."));
  return !Number.isNaN(numero) && numero !== 0;
}

/** O catálogo fechado de valores. O layout escolhe; aqui é onde eles moram. */
function resolverValor(chave: ChaveCampo, pedido: PedidoParaImpressao): string {
  const totais = pedido.totais ?? {};
  switch (chave) {
    case "filial":
      return texto(pedido.filial);
    case "codigoPedido":
      return texto(pedido.codigoPedido);
    case "tituloDocumento":
      return texto(pedido.tituloDocumento);
    case "data":
      return texto(pedido.data);
    case "periodo":
      return texto(pedido.periodo);
    case "caixa":
      return texto(pedido.caixa);
    case "operador":
      return texto(pedido.operador);
    case "entregador":
      return texto(pedido.entregador);
    case "numeroPessoas":
      return texto(pedido.numeroPessoas);
    case "identificacaoItem":
      if (pedido.tipoItem === 4 || pedido.tipoItem === 5) {
        return texto(pedido.nomeItem).toUpperCase();
      }
      return `DELIVERY - ${texto(pedido.codigoPedido)}`;
    case "lugarOuContaInteira":
      return Number(pedido.lugar)
        ? `Lugar ${Number(pedido.lugar)}`
        : "Conta Inteira";
    case "clienteNome":
      return texto(pedido.clienteNome);
    case "clienteTelefone":
      return texto(pedido.clienteTelefone);
    case "clienteEndereco":
      return texto(pedido.clienteEndereco);
    case "clienteComplemento":
      return texto(pedido.clienteComplemento);
    case "clienteObservacao":
      return texto(pedido.clienteObservacao);
    case "totalProdutos":
      return texto(totais.totalProdutos);
    case "descontoConta":
      return texto(totais.descontoConta);
    case "descontoProdutos":
      return texto(totais.descontoProdutos);
    case "subtotal":
      return texto(totais.subtotal);
    case "taxaServico":
      return texto(totais.taxaServico);
    case "taxaEntrada":
      return texto(totais.taxaEntrada);
    case "consumoMinimo":
      return texto(totais.consumoMinimo);
    case "taxaEstacionamento":
      return texto(totais.taxaEstacionamento);
    case "taxaEntrega":
      return texto(totais.taxaEntrega);
    case "total":
      return texto(totais.total);
    case "troco":
      return texto(totais.troco);
    case "outrasImpressoes":
      return (pedido.outrasImpressoes ?? []).join(",");
    case "permanencia":
      return texto(pedido.permanencia);
    case "totalPorPessoa":
      return texto(pedido.totalPorPessoa);
    // Só para condição: "temValor" nos pagamentos vira "existe pagamento?".
    case "pagamentos":
      return (totais.pagamentos ?? []).map((p) => p.nome).join(",");
    // Estes não têm valor vindo do pedido: o texto vem do próprio campo.
    case "assinatura":
    case "avisoNaoFiscal":
    case "textoFixo":
    case "linhaEmBranco":
    case "separador":
      return "";
    default:
      return "";
  }
}

function condicaoAtendida(
  condicao: CondicaoCampo | undefined,
  valor: string,
  pedido: PedidoParaImpressao
): boolean {
  switch (condicao) {
    case undefined:
    case "sempre":
      return true;
    case "temValor":
      return valor.trim().length > 0;
    case "mesaOuComanda":
      return pedido.tipoItem === 4 || pedido.tipoItem === 5;
    case "mesa":
      return pedido.tipoItem === 4;
    case "comanda":
      return pedido.tipoItem === 5;
    case "delivery":
      return pedido.tipoItem !== 4 && pedido.tipoItem !== 5;
    case "diferenteDeZero":
      return ehNumeroDiferenteDeZero(valor);
    default:
      return true;
  }
}

// ---------------------------------------------------------------------------
// Renderização dos campos
// ---------------------------------------------------------------------------

function novaLinha(
  textoDaLinha: string,
  campo: Pick<
    CampoComanda,
    "alinhamento" | "negrito" | "alturaDupla" | "larguraDupla"
  >
): LinhaComanda {
  return {
    texto: textoDaLinha,
    alinhamento: campo.alinhamento,
    negrito: campo.negrito,
    alturaDupla: campo.alturaDupla,
    larguraDupla: campo.larguraDupla,
  };
}

function linhaEmBranco(alinhamento: Alinhamento): LinhaComanda {
  return {
    texto: "",
    alinhamento,
    negrito: false,
    alturaDupla: false,
    larguraDupla: false,
  };
}

function renderizarCampo(
  campo: CampoComanda,
  pedido: PedidoParaImpressao,
  larguraColunas: number
): LinhaComanda[] {
  if (!campo.ativo) return [];

  const saida: LinhaComanda[] = [];

  // A condição vale para TODO campo, inclusive separador e linha em branco:
  // é o que faz sumir a régua de "Outras impressões" quando não há nenhuma.
  const valorBruto = resolverValor(campo.chave, pedido);
  const valorParaCondicao = campo.chaveCondicao
    ? resolverValor(campo.chaveCondicao, pedido)
    : valorBruto;
  if (!condicaoAtendida(campo.condicao, valorParaCondicao, pedido)) return [];

  if (campo.chave === "linhaEmBranco") {
    const quantas = Math.max(1, campo.linhasEmBrancoDepois ?? 1);
    for (let i = 0; i < quantas; i++) saida.push(linhaEmBranco(campo.alinhamento));
    return saida;
  }

  if (campo.chave === "separador") {
    const caractere = campo.texto && campo.texto.length > 0 ? campo.texto : "_";
    const repeticoes = campo.repeticoes ?? 41;
    // A régua também quebra: num papel de 58mm (32 colunas) uma régua de 41
    // caracteres vira duas linhas na impressora, e o preview tem que mostrar.
    for (const parte of quebrarTexto(
      caractere.repeat(repeticoes),
      larguraColunas,
      campo.larguraDupla
    )) {
      saida.push(novaLinha(parte, campo));
    }
    for (let i = 0; i < (campo.linhasEmBrancoDepois ?? 0); i++) {
      saida.push(linhaEmBranco(campo.alinhamento));
    }
    return saida;
  }

  if (campo.chave === "pagamentos") {
    const pagamentos = pedido.totais?.pagamentos ?? [];
    if (pagamentos.length === 0) return [];
    for (const pagamento of pagamentos) {
      const linhaTexto = `${pagamento.nome}: ${pagamento.valor}${
        campo.sufixo ?? ""
      }`;
      for (const parte of quebrarTexto(
        linhaTexto,
        larguraColunas,
        campo.larguraDupla
      )) {
        saida.push(novaLinha(parte, campo));
      }
    }
    for (let i = 0; i < (campo.linhasEmBrancoDepois ?? 0); i++) {
      saida.push(linhaEmBranco(campo.alinhamento));
    }
    return saida;
  }

  let valor = valorBruto;

  if (campo.maiusculas) valor = valor.toUpperCase();

  let linhaTexto: string;
  if (campo.chave === "textoFixo" || campo.chave === "assinatura" || campo.chave === "avisoNaoFiscal") {
    linhaTexto = campo.texto ?? "";
  } else if (campo.rotulo) {
    linhaTexto = `${campo.rotulo}: ${valor}`;
  } else {
    linhaTexto = valor;
  }

  // O desconto de conta ganha o nome entre parênteses, como hoje.
  if (campo.chave === "descontoConta" && pedido.totais?.nomeDescontoConta) {
    linhaTexto = `${campo.rotulo ?? "Desconto Conta"} (${
      pedido.totais.nomeDescontoConta
    }): ${valor}`;
  }

  if (campo.sufixo) linhaTexto += campo.sufixo;

  for (const parte of quebrarTexto(
    linhaTexto,
    larguraColunas,
    campo.larguraDupla
  )) {
    saida.push(novaLinha(parte, campo));
  }

  for (let i = 0; i < (campo.linhasEmBrancoDepois ?? 0); i++) {
    saida.push(linhaEmBranco(campo.alinhamento));
  }

  return saida;
}

function renderizarCampos(
  campos: CampoComanda[],
  pedido: PedidoParaImpressao,
  larguraColunas: number
): LinhaComanda[] {
  const saida: LinhaComanda[] = [];
  for (const campo of campos) {
    for (const linha of renderizarCampo(campo, pedido, larguraColunas)) {
      saida.push(linha);
    }
  }
  return saida;
}

// ---------------------------------------------------------------------------
// Renderização do bloco de itens (com as observações penduradas)
// ---------------------------------------------------------------------------

/** Corta como a via de conferência corta hoje: limite-3 caracteres + "...". */
function aplicarLimite(valor: string, limite: number | undefined): string {
  if (!limite || valor.length <= limite) return valor;
  return valor.substring(0, limite - 3) + "...";
}

function valorDaColuna(
  coluna: ColunaComanda,
  produto: ProdutoParaImpressao
): string {
  switch (coluna.chave) {
    case "lugar":
      return texto(produto.peopleNumber);
    case "codigo":
      return texto(produto.productCode);
    case "quantidade":
      return texto(produto.quantidade ?? 1);
    case "nomeProduto":
      return texto(produto.productName);
    case "precoProduto":
      return produto.productPrice === undefined
        ? ""
        : (
            Number(produto.productPrice) + Number(produto.productDiscount ?? 0)
          )
            .toFixed(2)
            .replace(".", ",");
    default:
      return "";
  }
}

function renderizarItens(
  bloco: BlocoComanda,
  blocoObservacoes: BlocoComanda | undefined,
  pedido: PedidoParaImpressao,
  larguraColunas: number
): LinhaComanda[] {
  const saida: LinhaComanda[] = [];
  const produtos = pedido.produtos ?? [];
  if (produtos.length === 0) return saida;

  const alinhamentoTabela: Alinhamento = bloco.alinhamentoTabela ?? "esquerda";
  const colunas = (bloco.colunas ?? []).filter(
    (coluna) =>
      coluna.ativo && condicaoAtendida(coluna.condicao, "", pedido)
  );
  if (colunas.length === 0) return saida;

  const empurrar = (campo: CampoComanda | undefined) => {
    if (!campo) return;
    for (const linha of renderizarCampo(campo, pedido, larguraColunas)) {
      saida.push(linha);
    }
  };

  empurrar(bloco.separadorAntesDoCabecalho);

  // Cabeçalho da tabela
  const celulasCabecalho: CelulaTabela[] = colunas.map((coluna) => ({
    texto: coluna.titulo,
    alinhamento: coluna.alinhamentoTitulo ?? coluna.alinhamento,
    largura: coluna.larguraTitulo ?? coluna.largura,
  }));
  for (const linha of montarTabela(celulasCabecalho, larguraColunas)) {
    saida.push({
      texto: linha,
      alinhamento: alinhamentoTabela,
      negrito: bloco.negritoCabecalho ?? false,
      alturaDupla: false,
      larguraDupla: false,
    });
  }

  empurrar(bloco.separadorDepoisDoCabecalho);

  const ordenados = bloco.ordenarPorLugar
    ? produtos
        .slice()
        .sort(
          (a, b) => Number(a.peopleNumber ?? 0) - Number(b.peopleNumber ?? 0)
        )
    : produtos;

  const prefixoObservacao = blocoObservacoes?.prefixoObservacao ?? "> ";
  const prefixoCombo = blocoObservacoes?.prefixoCombo ?? "* ";
  const rotuloDesconto = blocoObservacoes?.rotuloDescontoProduto ?? "Desconto";
  const observacoesLigadas = blocoObservacoes ? blocoObservacoes.ativo : true;
  const mostrarPrecoObservacao =
    blocoObservacoes?.mostrarPrecoObservacao ?? false;
  const mostrarDescontoProduto =
    blocoObservacoes?.mostrarDescontoProduto ?? false;

  for (const produto of ordenados) {
    const celulas: CelulaTabela[] = colunas.map((coluna) => ({
      texto: aplicarLimite(
        valorDaColuna(coluna, produto),
        coluna.limiteCaracteres
      ),
      alinhamento: coluna.alinhamento,
      largura: coluna.largura,
    }));
    for (const linha of montarTabela(celulas, larguraColunas)) {
      saida.push({
        texto: linha,
        alinhamento: alinhamentoTabela,
        negrito: bloco.negritoItem ?? false,
        alturaDupla: false,
        larguraDupla: false,
      });
    }

    if (observacoesLigadas) {
      for (const observacao of produto.productObservations ?? []) {
        const preco = Number(observacao.observationPrice ?? 0);
        const textoObs =
          mostrarPrecoObservacao && preco > 0
            ? `${prefixoObservacao}${observacao.observationName}   + ${preco
                .toFixed(2)
                .replace(".", ",")}`
            : `${prefixoObservacao}${observacao.observationName}`;
        for (const parte of quebrarTexto(textoObs, larguraColunas, false)) {
          saida.push({
            texto: parte,
            alinhamento: alinhamentoTabela,
            negrito: false,
            alturaDupla: false,
            larguraDupla: false,
          });
        }
      }

      for (const componente of produto.componentesCombo ?? []) {
        for (const parte of quebrarTexto(
          `${prefixoCombo}${componente}`,
          larguraColunas,
          false
        )) {
          saida.push({
            texto: parte,
            alinhamento: alinhamentoTabela,
            negrito: true,
            alturaDupla: false,
            larguraDupla: false,
          });
        }
      }

      const desconto = Number(produto.productDiscount ?? 0);
      if (mostrarDescontoProduto && desconto > 0) {
        saida.push({
          texto: `${rotuloDesconto}: ${desconto
            .toFixed(2)
            .replace(".", ",")}  `,
          alinhamento: "direita",
          negrito: false,
          alturaDupla: false,
          larguraDupla: false,
        });
      }
    }

    empurrar(bloco.separadorEntreItens);
  }

  empurrar(bloco.separadorFinal);

  return saida;
}

// ---------------------------------------------------------------------------
// Entrada pública
// ---------------------------------------------------------------------------

export function renderizarComanda(
  layout: LayoutComanda,
  pedido: PedidoParaImpressao
): LinhaComanda[] {
  const larguraColunas = layout.larguraColunas;
  const blocoObservacoes = layout.blocos.find(
    (bloco) => bloco.tipo === "observacoes"
  );

  const saida: LinhaComanda[] = [];
  for (const bloco of layout.blocos) {
    if (!bloco.ativo) continue;
    if (!condicaoAtendida(bloco.condicao, "", pedido)) continue;
    // O bloco de observações não imprime sozinho: ele configura o que o bloco
    // de itens pendura embaixo de cada produto (é assim que a cozinha lê hoje).
    if (bloco.tipo === "observacoes") continue;

    if (bloco.tipo === "itens") {
      for (const linha of renderizarItens(
        bloco,
        blocoObservacoes,
        pedido,
        larguraColunas
      )) {
        saida.push(linha);
      }
      continue;
    }

    for (const linha of renderizarCampos(bloco.campos, pedido, larguraColunas)) {
      saida.push(linha);
    }
  }

  return saida;
}

/** Texto puro das linhas — usado nas provas e na comparação com o legado. */
export function linhasComoTexto(linhas: LinhaComanda[]): string {
  return linhas.map((linha) => linha.texto).join("\n");
}
